/**
 * Google Tag Manager (GTM) Analytics Service
 *
 * This service provides a centralized way to track user interactions
 * and events across landing pages using Google Tag Manager.
 *
 * @see https://developers.google.com/tag-manager
 */

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

interface GTMEvent {
  event: string;
  [key: string]: any;
}

interface PageViewData {
  page_title?: string;
  page_location?: string;
  page_path?: string;
  [key: string]: any;
}

interface CTAClickData {
  cta_text: string;
  cta_location: string;
  cta_type?: 'primary' | 'secondary' | 'tertiary';
  destination_url?: string;
  [key: string]: any;
}

interface SectionViewData {
  section_name: string;
  section_position?: number;
  scroll_depth?: number;
  [key: string]: any;
}

interface FormData {
  form_name: string;
  form_type?: string;
  form_location?: string;
  [key: string]: any;
}

class GTMService {
  private gtmId: string;

  private isInitialized = false;

  private debugMode = false;

  constructor() {
    this.gtmId = import.meta.env.VITE_GOOGLE_TAG_MANAGER_ID || '';
    this.debugMode = import.meta.env.DEV || false;
  }

  /**
   * Initialize Google Tag Manager
   * Should be called once when the app starts
   */
  initialize(): void {
    if (!this.gtmId) {
      console.warn('GTM: No GTM ID provided. Tracking will not be initialized.');
      return;
    }

    if (this.isInitialized) {
      console.warn('GTM: Already initialized');
      return;
    }

    if (typeof window === 'undefined') {
      console.warn('GTM: Window object not available');
      return;
    }

    try {
      // Initialize dataLayer
      window.dataLayer = window.dataLayer || [];

      // Add GTM script
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtm.js?id=${this.gtmId}`;

      script.onload = () => {
        this.isInitialized = true;
        this.log('GTM initialized successfully', { gtmId: this.gtmId });
      };

      script.onerror = () => {
        console.error('GTM: Failed to load GTM script');
      };

      const firstScript = document.getElementsByTagName('script')[0];
      firstScript.parentNode?.insertBefore(script, firstScript);

      // Push initial GTM config
      window.dataLayer.push({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js'
      });
    } catch (error) {
      console.error('GTM: Initialization error', error);
    }
  }

  /**
   * Push a custom event to the data layer
   */
  private pushEvent(eventData: GTMEvent): void {
    if (!this.isInitialized) {
      console.warn('GTM: Not initialized. Event not tracked:', eventData);
      return;
    }

    try {
      window.dataLayer.push(eventData);
      this.log('Event tracked', eventData);
    } catch (error) {
      console.error('GTM: Error pushing event', error);
    }
  }

  /**
   * Track page view
   * Call this on route changes or when landing pages load
   */
  trackPageView(data: PageViewData = {}): void {
    const pageData = {
      event: 'page_view',
      page_title: document.title,
      page_location: window.location.href,
      page_path: window.location.pathname,
      ...data
    };

    this.pushEvent(pageData);
  }

  /**
   * Track CTA button clicks
   * Use this for "Get Started", "Sign Up", "Book Demo" buttons
   */
  trackCTAClick(data: CTAClickData): void {
    this.pushEvent({
      event: 'cta_click',
      ...data
    });
  }

  /**
   * Track section views (viewport visibility)
   * Useful for tracking which sections users actually see
   */
  trackSectionView(data: SectionViewData): void {
    this.pushEvent({
      event: 'section_view',
      ...data
    });
  }

  /**
   * Track scroll depth
   * Automatically tracks when users scroll to 25%, 50%, 75%, 100%
   */
  trackScrollDepth(percentage: number): void {
    this.pushEvent({
      event: 'scroll_depth',
      scroll_percentage: percentage
    });
  }

  /**
   * Track video interactions
   */
  trackVideoPlay(videoTitle: string, videoUrl?: string): void {
    this.pushEvent({
      event: 'video_play',
      video_title: videoTitle,
      video_url: videoUrl || ''
    });
  }

  /**
   * Track form submissions
   */
  trackFormSubmit(data: FormData): void {
    this.pushEvent({
      event: 'form_submit',
      ...data
    });
  }

  /**
   * Track form starts (when user begins filling out a form)
   */
  trackFormStart(data: FormData): void {
    this.pushEvent({
      event: 'form_start',
      ...data
    });
  }

  /**
   * Track navigation clicks
   */
  trackNavClick(linkText: string, linkUrl: string): void {
    this.pushEvent({
      event: 'nav_click',
      link_text: linkText,
      link_url: linkUrl
    });
  }

  /**
   * Track feature card interactions
   */
  trackFeatureClick(featureName: string, featureSection: string): void {
    this.pushEvent({
      event: 'feature_click',
      feature_name: featureName,
      feature_section: featureSection
    });
  }

  /**
   * Track testimonial views
   */
  trackTestimonialView(testimonialAuthor: string): void {
    this.pushEvent({
      event: 'testimonial_view',
      testimonial_author: testimonialAuthor
    });
  }

  /**
   * Track pricing plan selections
   */
  trackPlanSelect(planName: string, planPrice: string): void {
    this.pushEvent({
      event: 'plan_select',
      plan_name: planName,
      plan_price: planPrice
    });
  }

  /**
   * Track custom events
   * Use this for any custom tracking needs
   */
  trackCustomEvent(eventName: string, eventData: Record<string, any> = {}): void {
    this.pushEvent({
      event: eventName,
      ...eventData
    });
  }

  /**
   * Set user properties
   * Use this to identify users or set user attributes
   */
  setUserProperties(properties: Record<string, any>): void {
    this.pushEvent({
      event: 'user_properties',
      ...properties
    });
  }

  /**
   * Debug logging
   */
  private log(message: string, data?: any): void {
    if (this.debugMode) {
      console.log(`[GTM] ${message}`, data || '');
    }
  }
}

// Export singleton instance
export const gtmService = new GTMService();

// Export for use in components
export default gtmService;
